import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { createEditor, Transforms, Editor, Range, Text, Node } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import { buildPillText } from '../mentionUtils';

// --- Styled components ---

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const EditorWrapper = styled.div`
  font-family: inherit;
  font-size: inherit;
  line-height: 1.5;
  min-height: ${(props) => (props.$minRows || 3) * 1.5}em;
  padding: 8px;
  border: 1px solid #999;
  border-radius: 8px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  cursor: text;

  &:focus-within {
    border-color: #333;
  }

  [contenteditable] {
    outline: none;
  }
`;

const SuggestionsList = styled.ul`
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 180px;
  overflow-y: auto;
  width: 220px;
`;

const SuggestionItem = styled.li`
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.85rem;
  background: ${(props) => (props.$active ? '#f0f0f0' : 'transparent')};

  &:hover {
    background: #f0f0f0;
  }
`;

const Username = styled.span`
  font-weight: 600;
`;

const DisplayName = styled.span`
  color: #888;
  margin-left: 6px;
  font-weight: 300;
`;

// Pill styles (inline in the editor)
const PillSpan = styled.span`
  display: inline-flex;
  align-items: center;
  background: #222;
  color: #fff;
  border-radius: 3px;
  padding: 0 4px;
  margin: 0 1px;
  font-size: 0.8em;
  line-height: 1.4;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: baseline;
  cursor: default;
  user-select: none;
`;

const HintText = styled.div`
  font-size: 0.7rem;
  color: #bbb;
  margin-bottom: 4px;
  text-align: center;
`;

const UserPillSpan = styled.span`
  display: inline-flex;
  align-items: center;
  color: #0066cc;
  font-weight: 600;
  white-space: nowrap;
  cursor: default;
  user-select: none;
`;

// Music search sheet
const MusicOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9998;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: env(safe-area-inset-top, 0px);
  overflow-y: auto;

  @media (min-width: 601px) {
    align-items: center;
    padding-top: 0;
  }
`;

const MusicPanel = styled.div`
  position: relative;
  z-index: 9999;
  background: #fff;
  width: 100%;
  max-height: 70vh;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 400px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
`;

const MusicSearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #eee;
  font-size: 1rem;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }
`;

const MusicSuggestionsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 50vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const MusicSuggestionItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.85rem;
  background: ${(props) => (props.$active ? '#f0f0f0' : 'transparent')};

  &:hover {
    background: #f0f0f0;
  }
`;

const MusicThumb = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const MusicInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const MusicTitle = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MusicArtist = styled.div`
  color: #888;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MusicTypeBadge = styled.span`
  font-size: 0.65rem;
  color: #999;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.1rem 0.3rem;
  flex-shrink: 0;
`;

const MusicNoResults = styled.li`
  padding: 10px 12px;
  color: #999;
  font-size: 0.8rem;
  font-style: italic;
`;

const MusicLoading = styled.li`
  padding: 10px 12px;
  color: #999;
  font-size: 0.8rem;
`;

// --- Slate helpers ---

function withMentions(editor) {
  var isInline = editor.isInline;
  var isVoid = editor.isVoid;

  editor.isInline = function (element) {
    return element.type === 'music-mention' ||
      element.type === 'user-mention' ||
      element.type === 'hashtag-mention'
      ? true
      : isInline(element);
  };

  editor.isVoid = function (element) {
    return element.type === 'music-mention' ||
      element.type === 'user-mention' ||
      element.type === 'hashtag-mention'
      ? true
      : isVoid(element);
  };

  return editor;
}

// Extract mentions from Slate tree in document order
function extractMentionsInOrder(nodes) {
  var mentions = [];
  for (var i = 0; i < nodes.length; i++) {
    var node = nodes[i];
    for (var j = 0; j < (node.children || []).length; j++) {
      var child = node.children[j];
      if (child.type === 'music-mention') {
        mentions.push({
          title: child.title,
          artistName: child.artistName,
          type: child.mentionType,
        });
      }
    }
  }
  return mentions;
}

// Tokens for API submission ({mention:0}, {mention:1}, etc.)
function serializeForSubmit(nodes) {
  var mentionIdx = 0;
  var text = '';
  for (var i = 0; i < nodes.length; i++) {
    var node = nodes[i];
    var children = node.children || [];
    if (i > 0) text += '\n';
    for (var j = 0; j < children.length; j++) {
      var child = children[j];
      if (child.type === 'music-mention') {
        text += '{mention:' + mentionIdx + '}';
        mentionIdx++;
      } else if (child.type === 'user-mention') {
        text += '{@' + child.username + '}';
      } else if (child.type === 'hashtag-mention') {
        text += '{#' + child.tagName + '}';
      } else {
        text += child.text || '';
      }
    }
  }
  return text;
}

function serializeForDisplay(nodes) {
  var text = '';
  for (var i = 0; i < nodes.length; i++) {
    var node = nodes[i];
    var children = node.children || [];
    if (i > 0) text += '\n';
    for (var j = 0; j < children.length; j++) {
      var child = children[j];
      if (child.type === 'music-mention') {
        text += buildPillText({
          type: child.mentionType,
          title: child.title,
          artistName: child.artistName,
        });
      } else if (child.type === 'user-mention') {
        text += '{@' + child.username + '}';
      } else if (child.type === 'hashtag-mention') {
        text += '{#' + child.tagName + '}';
      } else {
        text += child.text || '';
      }
    }
  }
  return text;
}

// Deserialize plain text to Slate nodes, converting @mentions and [bracket] pills
function deserialize(text, musicMentions) {
  var mentionArr = musicMentions || [];
  var lines = (text || '').split('\n');
  return lines.map(function (line) {
    var children = [];
    var remaining = line;

    while (remaining.length > 0) {
      // Check for [Title - Artist] or [Artist] bracket (music mention pill)
      var foundMention = false;
      for (var mi = 0; mi < mentionArr.length; mi++) {
        var m = mentionArr[mi];
        if (m && m.pillText && remaining.indexOf(m.pillText) === 0) {
          children.push({
            type: 'music-mention',
            title: m.title,
            artistName: m.artistName,
            mentionType: m.type || 'song',
            children: [{ text: '' }],
          });
          remaining = remaining.slice(m.pillText.length);
          foundMention = true;
          break;
        }
      }
      if (foundMention) continue;

      // Check for {@username} (confirmed mention)
      var atMatch = remaining.match(/^\{@([a-zA-Z0-9_]+)\}/);
      // Also support legacy bare @username for existing posts
      if (!atMatch) atMatch = remaining.match(/^@([a-zA-Z0-9_]+)/);
      if (atMatch) {
        children.push({
          type: 'user-mention',
          username: atMatch[1],
          children: [{ text: '' }],
        });
        remaining = remaining.slice(atMatch[0].length);
        continue;
      }

      // Check for {#hashtag} (confirmed hashtag)
      var hashMatch = remaining.match(/^\{#([a-zA-Z0-9_]+)\}/);
      // Also support legacy bare #hashtag for existing posts
      if (!hashMatch) hashMatch = remaining.match(/^#([a-zA-Z0-9_]+)/);
      if (hashMatch) {
        children.push({
          type: 'hashtag-mention',
          tagName: hashMatch[1],
          children: [{ text: '' }],
        });
        remaining = remaining.slice(hashMatch[0].length);
        continue;
      }

      // Find next special token
      var nextAt = remaining.indexOf('{@');
      if (nextAt === -1) nextAt = remaining.indexOf('@');
      var nextHash = remaining.indexOf('{#');
      if (nextHash === -1) nextHash = remaining.indexOf('#');
      var nextBracket = -1;
      for (var bi = 0; bi < mentionArr.length; bi++) {
        if (mentionArr[bi] && mentionArr[bi].pillText) {
          var bIdx = remaining.indexOf(mentionArr[bi].pillText);
          if (bIdx > 0 && (nextBracket === -1 || bIdx < nextBracket)) nextBracket = bIdx;
        }
      }
      var candidates = [nextAt, nextHash, nextBracket].filter(function (v) {
        return v > 0;
      });
      var nextSpecial = candidates.length > 0 ? Math.min.apply(null, candidates) : -1;

      if (nextSpecial > 0) {
        children.push({ text: remaining.slice(0, nextSpecial) });
        remaining = remaining.slice(nextSpecial);
      } else {
        children.push({ text: remaining });
        remaining = '';
      }
    }

    if (children.length === 0) children.push({ text: '' });
    return { type: 'paragraph', children: children };
  });
}

// --- Slate element renderers ---

function MusicMentionElement(props) {
  var isArtist = props.element.mentionType === 'artist';
  return (
    <PillSpan {...props.attributes} contentEditable={false}>
      {isArtist ? (
        <span style={{ fontWeight: 700 }}>{props.element.artistName || props.element.title}</span>
      ) : (
        <>
          <span style={{ fontWeight: 700, fontStyle: 'italic' }}>{props.element.title}</span>
          <span style={{ margin: '0 4px' }}>-</span>
          <span style={{ fontWeight: 700 }}>{props.element.artistName}</span>
        </>
      )}
      {props.children}
    </PillSpan>
  );
}

function UserMentionElement(props) {
  return (
    <UserPillSpan {...props.attributes} contentEditable={false}>
      @{props.element.username}
      {props.children}
    </UserPillSpan>
  );
}

function HashtagMentionElement(props) {
  return (
    <span {...props.attributes} contentEditable={false}>
      <span style={{ color: '#0066cc', fontWeight: 600 }}>#{props.element.tagName}</span>
      {props.children}
    </span>
  );
}

var MAX_MUSIC_MENTIONS = 10;

// --- Main component ---

export default function MentionInput(props) {
  var {
    value,
    onChange,
    onKeyDown,
    inputRef,
    musicMentions,
    onMusicMentionsChange,
    getSubmitTextRef,
    minRows,
    ...rest
  } = props;
  var { authTokens } = useAuth();
  var currentUserId = authTokens?.user?.id;

  var [editor] = useState(function () {
    return withMentions(withHistory(withReact(createEditor())));
  });

  // Track whether we're updating from internal change vs external prop
  var internalChangeRef = useRef(false);
  var lastSerializedRef = useRef(value || '');

  // @mention state
  var [suggestions, setSuggestions] = useState([]);
  var [activeIndex, setActiveIndex] = useState(0);
  var [mentionQuery, setMentionQuery] = useState(null);
  var [mentionTarget, setMentionTarget] = useState(null);
  var [listPosition, setListPosition] = useState({ top: 0, left: 0 });
  var debounceRef = useRef(null);

  // #hashtag state
  var [hashtagSuggestions, setHashtagSuggestions] = useState([]);
  var [hashtagActiveIndex, setHashtagActiveIndex] = useState(0);
  var [hashtagQuery, setHashtagQuery] = useState(null);
  var [hashtagTarget, setHashtagTarget] = useState(null);
  var [hashtagListPosition, setHashtagListPosition] = useState({ top: 0, left: 0 });
  var hashtagDebounceRef = useRef(null);

  // +music state
  var [musicSearchOpen, setMusicSearchOpen] = useState(false);
  var [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  var [musicSearchText, setMusicSearchText] = useState('');
  var [musicSuggestions, setMusicSuggestions] = useState([]);
  var [musicActiveIndex, setMusicActiveIndex] = useState(0);
  var [musicLoading, setMusicLoading] = useState(false);
  var musicDebounceRef = useRef(null);
  var musicSearchRef = useRef(null);
  // Track visual viewport height (shrinks when mobile keyboard is open)
  useEffect(
    function () {
      if (!musicSearchOpen) return;
      function update() {
        var vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
        setViewportHeight(vh);
      }
      update();
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', update);
        return function () {
          window.visualViewport.removeEventListener('resize', update);
        };
      }
    },
    [musicSearchOpen]
  );

  // Initialize / sync Slate value from string prop
  var [slateValue, setSlateValue] = useState(function () {
    return deserialize(value || '', musicMentions);
  });
  var slateValueRef = useRef(slateValue);

  // Expose getSubmitData so parents can get tokenized text + ordered mentions at submit time
  useEffect(
    function () {
      if (getSubmitTextRef) {
        getSubmitTextRef.current = function () {
          return {
            text: serializeForSubmit(slateValueRef.current),
            mentions: extractMentionsInOrder(slateValueRef.current),
          };
        };
      }
    },
    [getSubmitTextRef]
  );

  // Only reset Slate when the parent clears the value externally (e.g., after submit)
  // Skip if the change came from the user editing in Slate (internalChangeRef)
  useEffect(
    function () {
      if (internalChangeRef.current) {
        internalChangeRef.current = false;
        return;
      }
      var currentText = value || '';
      if (currentText === '' && lastSerializedRef.current !== '') {
        lastSerializedRef.current = '';
        var empty = [{ type: 'paragraph', children: [{ text: '' }] }];
        editor.children = empty;
        editor.selection = {
          anchor: { path: [0, 0], offset: 0 },
          focus: { path: [0, 0], offset: 0 },
        };
        editor.history = { undos: [], redos: [] };
        setSlateValue(empty);
      }
    },
    [value]
  );

  // Handle Slate onChange
  function handleSlateChange(newValue) {
    setSlateValue(newValue);
    slateValueRef.current = newValue;

    // Skip serialization and sync on selection-only changes
    var hasContentChange = editor.operations.some(function (op) {
      return op.type !== 'set_selection';
    });
    if (!hasContentChange) {
      detectAtMention();
      return;
    }

    var serialized = serializeForDisplay(newValue);
    if (serialized !== lastSerializedRef.current) {
      lastSerializedRef.current = serialized;
      internalChangeRef.current = true;
      if (onChange) {
        onChange({ target: { value: serialized, name: rest.name } });
      }
    }
    // Sync: only remove mentions from state when pills are deleted from editor
    if (onMusicMentionsChange && (musicMentions || []).length > 0) {
      var editorPillCount = 0;
      for (var ni = 0; ni < newValue.length; ni++) {
        var para = newValue[ni];
        for (var ci = 0; ci < (para.children || []).length; ci++) {
          if (para.children[ci].type === 'music-mention') editorPillCount++;
        }
      }
      var currentMentions = musicMentions || [];
      // Only sync when pills were REMOVED (editor has fewer than state)
      if (editorPillCount < currentMentions.length) {
        var editorMentionKeys = [];
        for (var ni2 = 0; ni2 < newValue.length; ni2++) {
          var para2 = newValue[ni2];
          for (var ci2 = 0; ci2 < (para2.children || []).length; ci2++) {
            var child2 = para2.children[ci2];
            if (child2.type === 'music-mention') {
              editorMentionKeys.push(
                child2.title + '|' + child2.artistName + '|' + child2.mentionType
              );
            }
          }
        }
        var remaining = currentMentions.filter(function (m) {
          var key = m.title + '|' + m.artistName + '|' + m.type;
          return editorMentionKeys.indexOf(key) !== -1;
        });
        onMusicMentionsChange(remaining);
      }
    }
    // Detect @mention and #hashtag triggers
    detectAtMention();
    detectHashtag();
  }

  // Detect @mention at cursor
  function detectAtMention() {
    try {
      var selection = editor.selection;
      if (!selection || !Range.isCollapsed(selection)) {
        setMentionQuery(null);
        setMentionTarget(null);
        return;
      }
      var [start] = Range.edges(selection);
      // Only detect in text nodes, not void nodes
      var node = Node.get(editor, start.path);
      if (!Text.isText(node)) {
        setMentionQuery(null);
        setMentionTarget(null);
        return;
      }
      var textBeforeCursor = node.text.slice(0, start.offset);
      var match = textBeforeCursor.match(/(^|[^a-zA-Z0-9_])@([a-zA-Z0-9_]*)$/);
      if (match) {
        var mentionStart = textBeforeCursor.lastIndexOf('@' + match[2]);
        var startPoint = { path: start.path, offset: mentionStart };
        setMentionQuery(match[2]);
        setMentionTarget({ anchor: startPoint, focus: start });
      } else {
        setMentionQuery(null);
        setMentionTarget(null);
      }
    } catch (e) {
      setMentionQuery(null);
      setMentionTarget(null);
    }
  }

  // Fetch @mention suggestions
  useEffect(
    function () {
      if (mentionQuery === null) {
        setSuggestions([]);
        return;
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(function () {
        api
          .get('/api/users/search?q=' + encodeURIComponent(mentionQuery))
          .then(function (res) {
            var users = (res.data.users || []).filter(function (u) {
              return u.id !== currentUserId;
            });
            setSuggestions(users);
            setActiveIndex(0);
          })
          .catch(function () {
            setSuggestions([]);
          });
      }, 150);
      return function () {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    },
    [mentionQuery]
  );

  // Position @mention dropdown
  useEffect(
    function () {
      if (mentionTarget) {
        try {
          var domRange = ReactEditor.toDOMRange(editor, mentionTarget);
          var rect = domRange.getBoundingClientRect();
          setListPosition({ top: rect.bottom + 2, left: rect.left });
        } catch (e) {}
      }
    },
    [mentionTarget, suggestions]
  );

  // Detect #hashtag at cursor
  function detectHashtag() {
    try {
      var selection = editor.selection;
      if (!selection || !Range.isCollapsed(selection)) {
        setHashtagQuery(null);
        setHashtagTarget(null);
        return;
      }
      var [start] = Range.edges(selection);
      var node = Node.get(editor, start.path);
      if (!Text.isText(node)) {
        setHashtagQuery(null);
        setHashtagTarget(null);
        return;
      }
      var textBeforeCursor = node.text.slice(0, start.offset);
      var match = textBeforeCursor.match(/(^|[^a-zA-Z0-9_])#([a-zA-Z0-9_]*)$/);
      if (match) {
        var hashStart = textBeforeCursor.lastIndexOf('#' + match[2]);
        var startPoint = { path: start.path, offset: hashStart };
        setHashtagQuery(match[2]);
        setHashtagTarget({ anchor: startPoint, focus: start });
      } else {
        setHashtagQuery(null);
        setHashtagTarget(null);
      }
    } catch (e) {
      setHashtagQuery(null);
      setHashtagTarget(null);
    }
  }

  // Fetch #hashtag suggestions
  useEffect(
    function () {
      if (hashtagQuery === null || hashtagQuery.length === 0) {
        setHashtagSuggestions([]);
        return;
      }
      if (hashtagDebounceRef.current) clearTimeout(hashtagDebounceRef.current);
      hashtagDebounceRef.current = setTimeout(function () {
        api
          .get('/api/tags/search?q=' + encodeURIComponent(hashtagQuery))
          .then(function (res) {
            var tags = res.data || [];
            setHashtagSuggestions(tags);
            setHashtagActiveIndex(0);
          })
          .catch(function () {
            setHashtagSuggestions([]);
          });
      }, 150);
      return function () {
        if (hashtagDebounceRef.current) clearTimeout(hashtagDebounceRef.current);
      };
    },
    [hashtagQuery]
  );

  // Position #hashtag dropdown
  useEffect(
    function () {
      if (hashtagTarget) {
        try {
          var domRange = ReactEditor.toDOMRange(editor, hashtagTarget);
          var rect = domRange.getBoundingClientRect();
          setHashtagListPosition({ top: rect.bottom + 2, left: rect.left });
        } catch (e) {}
      }
    },
    [hashtagTarget, hashtagSuggestions]
  );

  // Insert #hashtag mention
  function insertHashtag(tagName) {
    if (!hashtagTarget) return;
    Transforms.select(editor, hashtagTarget);
    Transforms.delete(editor);
    var hashNode = {
      type: 'hashtag-mention',
      tagName: tagName.toLowerCase(),
      children: [{ text: '' }],
    };
    Transforms.insertNodes(editor, hashNode);
    Transforms.move(editor);
    Transforms.insertText(editor, ' ');
    setHashtagSuggestions([]);
    setHashtagQuery(null);
    setHashtagTarget(null);
    try {
      ReactEditor.focus(editor);
    } catch (e) {}
  }

  // Insert @user mention
  function insertUserMention(user) {
    if (!mentionTarget) return;
    Transforms.select(editor, mentionTarget);
    Transforms.delete(editor);
    var mentionNode = {
      type: 'user-mention',
      username: user.username,
      children: [{ text: '' }],
    };
    Transforms.insertNodes(editor, mentionNode);
    Transforms.move(editor);
    // Insert a space after
    Transforms.insertText(editor, ' ');
    setSuggestions([]);
    setMentionQuery(null);
    setMentionTarget(null);
    try {
      ReactEditor.focus(editor);
    } catch (e) {}
  }

  // Insert music mention pill
  function insertMusicMention(item) {
    var isArtist = item.type === 'artist';
    var title = isArtist ? item.artist : item.song_title || item.album_title;
    var pillText = buildPillText({
      type: isArtist ? 'artist' : item.song_title ? 'song' : 'album',
      title: title,
      artistName: item.artist,
    });

    try {
      // Ensure editor has content to insert into
      if (editor.children.length === 0) {
        Transforms.insertNodes(
          editor,
          { type: 'paragraph', children: [{ text: '' }] },
          { at: [0] }
        );
      }

      // Ensure we have a valid selection
      if (!editor.selection) {
        Transforms.select(editor, Editor.end(editor, []));
      }

      // Remove the "+" character that was inserted
      if (editor.selection) {
        var [start] = Range.edges(editor.selection);
        var currentNode = Node.get(editor, start.path);
        if (Text.isText(currentNode)) {
          var plusIdx = currentNode.text.lastIndexOf('+');
          if (plusIdx !== -1) {
            Transforms.delete(editor, {
              at: {
                anchor: { path: start.path, offset: plusIdx },
                focus: { path: start.path, offset: plusIdx + 1 },
              },
            });
          }
        }
      }

      // Insert the mention node
      var mentionNode = {
        type: 'music-mention',
        title: title,
        artistName: item.artist,
        mentionType: isArtist ? 'artist' : item.song_title ? 'song' : 'album',
        children: [{ text: '' }],
      };
      Transforms.insertNodes(editor, mentionNode);
      Transforms.move(editor);
      Transforms.insertText(editor, ' ');
    } catch (e) {
      console.warn('insertMusicMention error:', e);
    }

    if (onMusicMentionsChange) {
      var newMention = {
        title: title,
        artistName: item.artist,
        type: isArtist ? 'artist' : item.song_title ? 'song' : 'album',
        artworkUrl: item.artwork_url,
        isrc: isArtist ? null : item.isrc || null,
        upc: isArtist ? null : item.upc || null,
        releaseDate: isArtist ? null : item.releaseDate || null,
        pillText: pillText,
      };
      onMusicMentionsChange((musicMentions || []).concat([newMention]));
    }

    setMusicSearchOpen(false);
    setMusicSearchText('');
    setMusicSuggestions([]);
    try {
      ReactEditor.focus(editor);
    } catch (e) {}
  }

  // Fetch music suggestions
  useEffect(
    function () {
      if (!musicSearchOpen || musicSearchText.length === 0) {
        setMusicSuggestions([]);
        setMusicLoading(false);
        return;
      }
      if (musicDebounceRef.current) clearTimeout(musicDebounceRef.current);
      setMusicLoading(true);
      musicDebounceRef.current = setTimeout(function () {
        var songsAlbumsPromise = api
          .get('/api/search', {
            params: { term: musicSearchText, types: 'songs,albums', limit: 10 },
          })
          .then(function (res) {
            return res.data || [];
          })
          .catch(function () {
            return [];
          });

        var artistsPromise = api
          .get('/api/search/artists', { params: { q: musicSearchText } })
          .then(function (res) {
            return (res.data || []).map(function (a) {
              return {
                artist: a.artist,
                type: 'artist',
                artwork_url: a.artworkURL || '',
                song_title: null,
                album_title: null,
                isrc: null,
                upc: null,
              };
            });
          })
          .catch(function () {
            return [];
          });

        Promise.all([songsAlbumsPromise, artistsPromise]).then(function (results) {
          // Interleave: top artist, then alternate songs/albums with remaining artists
          var songsAlbums = results[0];
          var artists = results[1].slice(0, 3);
          var combined = [];
          var ai = 0;
          var si = 0;
          // First result: top artist if available
          if (ai < artists.length) {
            combined.push(artists[ai]);
            ai++;
          }
          // Then interleave: 2 songs/albums per 1 artist
          while (si < songsAlbums.length || ai < artists.length) {
            if (si < songsAlbums.length) {
              combined.push(songsAlbums[si]);
              si++;
            }
            if (si < songsAlbums.length) {
              combined.push(songsAlbums[si]);
              si++;
            }
            if (ai < artists.length) {
              combined.push(artists[ai]);
              ai++;
            }
          }
          setMusicSuggestions(combined);
          setMusicActiveIndex(0);
          setMusicLoading(false);
        });
      }, 300);
      return function () {
        if (musicDebounceRef.current) clearTimeout(musicDebounceRef.current);
      };
    },
    [musicSearchText, musicSearchOpen]
  );

  // Auto-focus music search input (delay slightly to avoid capturing the + keystroke)
  useEffect(
    function () {
      if (musicSearchOpen && musicSearchRef.current) {
        setTimeout(function () {
          if (musicSearchRef.current) {
            musicSearchRef.current.focus();
            musicSearchRef.current.value = '';
            setMusicSearchText('');
          }
        }, 50);
      }
    },
    [musicSearchOpen]
  );

  // Handle keydown in editor
  function handleEditorKeyDown(event) {
    // @mention keyboard nav
    if (suggestions.length > 0 && mentionQuery !== null) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex(function (i) {
          return (i + 1) % suggestions.length;
        });
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex(function (i) {
          return (i - 1 + suggestions.length) % suggestions.length;
        });
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insertUserMention(suggestions[activeIndex]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setSuggestions([]);
        setMentionQuery(null);
        setMentionTarget(null);
        return;
      }
    }

    // #hashtag keyboard nav
    if (hashtagSuggestions.length > 0 && hashtagQuery !== null) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHashtagActiveIndex(function (i) {
          return (i + 1) % hashtagSuggestions.length;
        });
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHashtagActiveIndex(function (i) {
          return (i - 1 + hashtagSuggestions.length) % hashtagSuggestions.length;
        });
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insertHashtag(hashtagSuggestions[hashtagActiveIndex]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setHashtagSuggestions([]);
        setHashtagQuery(null);
        setHashtagTarget(null);
        return;
      }
    }

    // + trigger for music search (only at word boundary: start of text, after space/newline)
    if (event.key === '+' || (event.key === '=' && event.shiftKey)) {
      if ((musicMentions || []).length < MAX_MUSIC_MENTIONS) {
        var atBoundary = false;
        try {
          var sel = editor.selection;
          if (sel && Range.isCollapsed(sel)) {
            var pt = Range.start(sel);
            var nd = Node.get(editor, pt.path);
            if (Text.isText(nd)) {
              atBoundary = pt.offset === 0 || /\s/.test(nd.text.charAt(pt.offset - 1));
            }
          }
        } catch (e) {
          atBoundary = true; // empty editor, treat as boundary
        }
        if (atBoundary) {
          setTimeout(function () {
            setMusicSearchOpen(true);
            setMusicSearchText('');
          }, 0);
        }
      }
    }

    // Escape closes music search
    if (musicSearchOpen && event.key === 'Escape') {
      event.preventDefault();
      setMusicSearchOpen(false);
      setMusicSearchText('');
      setMusicSuggestions([]);
      return;
    }

    // Pass through
    if (onKeyDown) onKeyDown(event);
  }

  // Render elements
  var renderElement = useCallback(function (props) {
    switch (props.element.type) {
      case 'music-mention':
        return <MusicMentionElement {...props} />;
      case 'user-mention':
        return <UserMentionElement {...props} />;
      case 'hashtag-mention':
        return <HashtagMentionElement {...props} />;
      default:
        return (
          <p style={{ margin: 0 }} {...props.attributes}>
            {props.children}
          </p>
        );
    }
  }, []);

  // Render leaves (plain text)
  var renderLeaf = useCallback(function (props) {
    return <span {...props.attributes}>{props.children}</span>;
  }, []);

  var showAtSuggestions = mentionQuery !== null && suggestions.length > 0;
  var showHashtagSuggestions =
    hashtagQuery !== null && hashtagQuery.length > 0 && hashtagSuggestions.length > 0;

  return (
    <Wrapper>
      {(musicMentions || []).length === 0 && (
        <HintText>Use @ to mention people, + to mention music, # to add a tag</HintText>
      )}
      <EditorWrapper
        $minRows={minRows}
        onClick={function () {
          try {
            if (editor.children.length === 0) {
              Transforms.insertNodes(
                editor,
                { type: 'paragraph', children: [{ text: '' }] },
                { at: [0] }
              );
            }
            try {
              ReactEditor.focus(editor);
            } catch (e) {}
          } catch (e) {}
        }}
        style={rest.style}
      >
        <Slate editor={editor} initialValue={slateValue} onChange={handleSlateChange}>
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onKeyDown={handleEditorKeyDown}
            placeholder={rest.placeholder || ''}
            autoFocus={rest.autoFocus}
            readOnly={rest.disabled}
            style={{ outline: 'none', minHeight: (minRows || 3) * 1.5 + 'em' }}
          />
        </Slate>
      </EditorWrapper>
      {showAtSuggestions &&
        ReactDOM.createPortal(
          <SuggestionsList style={{ top: listPosition.top, left: listPosition.left }}>
            {suggestions.slice(0, 8).map(function (user, i) {
              return (
                <SuggestionItem
                  key={user.id}
                  $active={i === activeIndex}
                  onMouseDown={function (e) {
                    e.preventDefault();
                    insertUserMention(user);
                  }}
                >
                  <Username>@{user.username}</Username>
                  {user.displayName && <DisplayName>{user.displayName}</DisplayName>}
                </SuggestionItem>
              );
            })}
          </SuggestionsList>,
          document.body
        )}
      {showHashtagSuggestions &&
        ReactDOM.createPortal(
          <SuggestionsList style={{ top: hashtagListPosition.top, left: hashtagListPosition.left }}>
            {hashtagSuggestions.slice(0, 8).map(function (tag, i) {
              return (
                <SuggestionItem
                  key={tag}
                  $active={i === hashtagActiveIndex}
                  onMouseDown={function (e) {
                    e.preventDefault();
                    insertHashtag(tag);
                  }}
                >
                  <Username>#{tag}</Username>
                </SuggestionItem>
              );
            })}
          </SuggestionsList>,
          document.body
        )}
      {musicSearchOpen &&
        ReactDOM.createPortal(
          <MusicOverlay
            style={{ paddingBottom: Math.max(0, window.innerHeight - viewportHeight) }}
            onClick={function () {
              setMusicSearchOpen(false);
              setMusicSearchText('');
              setMusicSuggestions([]);
              try {
                ReactEditor.focus(editor);
              } catch (e) {}
            }}
          >
            <MusicPanel
              onClick={function (e) {
                e.stopPropagation();
              }}
            >
              <MusicSearchInput
                ref={musicSearchRef}
                placeholder="Search songs, albums, and artists..."
                value={musicSearchText}
                onChange={function (e) {
                  setMusicSearchText(e.target.value);
                }}
                onKeyDown={function (e) {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setMusicSearchOpen(false);
                    setMusicSearchText('');
                    setMusicSuggestions([]);
                    try {
                      ReactEditor.focus(editor);
                    } catch (e) {}
                  } else if (e.key === 'ArrowDown' && musicSuggestions.length > 0) {
                    e.preventDefault();
                    setMusicActiveIndex(function (i) {
                      return (i + 1) % musicSuggestions.length;
                    });
                  } else if (e.key === 'ArrowUp' && musicSuggestions.length > 0) {
                    e.preventDefault();
                    setMusicActiveIndex(function (i) {
                      return (i - 1 + musicSuggestions.length) % musicSuggestions.length;
                    });
                  } else if (
                    (e.key === 'Enter' || e.key === 'Tab') &&
                    musicSuggestions.length > 0
                  ) {
                    e.preventDefault();
                    insertMusicMention(musicSuggestions[musicActiveIndex]);
                  }
                }}
              />
              <MusicSuggestionsList>
                {musicLoading && musicSuggestions.length === 0 && (
                  <MusicLoading>Searching...</MusicLoading>
                )}
                {!musicLoading && musicSuggestions.length === 0 && musicSearchText.length > 0 && (
                  <MusicNoResults>No results for "{musicSearchText}"</MusicNoResults>
                )}
                {musicSuggestions.map(function (item, i) {
                  var isArtist = item.type === 'artist';
                  var title = isArtist ? item.artist : item.song_title || item.album_title;
                  var typeLabel = isArtist ? 'Artist' : item.song_title ? 'Song' : 'Album';
                  var artworkUrl = (item.artwork_url || '').replace('{w}x{h}', '60x60');
                  return (
                    <MusicSuggestionItem
                      key={i}
                      ref={function (el) {
                        if (i === musicActiveIndex && el) el.scrollIntoView({ block: 'nearest' });
                      }}
                      $active={i === musicActiveIndex}
                      onClick={function (e) {
                        e.stopPropagation();
                        insertMusicMention(item);
                      }}
                    >
                      {artworkUrl && <MusicThumb src={artworkUrl} alt={title} />}
                      <MusicInfo>
                        <MusicTitle>{title}</MusicTitle>
                        {!isArtist && <MusicArtist>{item.artist}</MusicArtist>}
                      </MusicInfo>
                      <MusicTypeBadge>{typeLabel}</MusicTypeBadge>
                    </MusicSuggestionItem>
                  );
                })}
              </MusicSuggestionsList>
            </MusicPanel>
          </MusicOverlay>,
          document.body
        )}
    </Wrapper>
  );
}
